<h2 mat-dialog-title>
    Organization Hierarchy

    <!-- Help Button -->
    <button mat-icon-button (click)="openHelpDialog()">
        <mat-icon>help</mat-icon>
    </button>

    <!-- Close button. -->
    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content>
    <div class="view-container m-3 w-100">
        <div class="view-header">
            <span class="viewer-title-spacer">
                <!-- Select or create new position -->
                <shared-searchable-select
                    class="flex-1-auto"
                    [displayValue]="false"
                    [appearance]="'outline'"
                    [classes]="['w-75']"
                    [label]="'Search org part'"
                    [data]="searchablePositions"
                    [control]="positionControl"
                    (selected)="onSelectInitialPart($event)"
                    [multiple]="false"
                ></shared-searchable-select>

                <button
                    mat-stroked-button
                    class="ml-3"
                    color="accent"
                    [disabled]="!positionControl.value"
                    (click)="onClear()"
                >
                    Clear<mat-icon>clear</mat-icon>
                </button>
            </span>

            <!-- Expand/Collapse Controls -->
            <div class="tree-controls">
                <button mat-raised-button color="primary" (click)="expandAll()" title="Expand all">
                    <mat-icon>expand_more</mat-icon>
                    <span class="ctrl-label">Expand All</span>
                </button>
                <button mat-raised-button color="accent" (click)="collapseAll()" title="Collapse all">
                    <mat-icon>chevron_right</mat-icon>
                    <span class="ctrl-label">Collapse All</span>
                </button>

                <div class="divider"></div>

                <span class="level-label">Show to:</span>
                <button mat-stroked-button class="level-btn" (click)="collapseToLevel(0)" title="Show only executives">
                    Executives
                </button>
                <button
                    mat-stroked-button
                    class="level-btn"
                    (click)="collapseToLevel(1)"
                    title="Show executives and divisions"
                >
                    Divisions
                </button>
                <button
                    mat-stroked-button
                    class="level-btn"
                    (click)="collapseToLevel(2)"
                    title="Show down to departments"
                >
                    Departments
                </button>
                <button mat-stroked-button class="level-btn" (click)="collapseToLevel(3)" title="Show down to sections">
                    Sections
                </button>
            </div>
        </div>

        <div class="view-content fix-height">
            <div class="mh-5px">
                @if(showLoader) {
                <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                }
            </div>

            <div class="d-flex flex-just-between h-100 hierarchy-split">
                <mat-tree
                    [dataSource]="dataSource"
                    [treeControl]="treeControlSecurityGroups"
                    class="flex-50 o-y-scroll"
                >
                    <mat-tree-node
                        *matTreeNodeDef="let node"
                        matTreeNodePadding
                        (mouseenter)="onHoverPosition(node)"
                        (click)="onClickPosition(node)"
                        [ngClass]="{
              'is-hovered': node.key === hoveredPosition?.key, 
              'is-selected': node.key === selectedPosition?.key,
              'is-post': node.partType === 'post' || node.data?.partType === 'post',
              'is-executive': node.partType === 'executive' || node.data?.partType === 'executive',
              'has-color': (node.data?.colour || (node.data && node.data.colour)) && 
                          node.data.partType !== 'section' && node.data.partType !== 'unit' &&
                          node.partType !== 'section' && node.partType !== 'unit',
              'is-division': node.partType === 'division' || node.data?.partType === 'division',
              'is-department': node.partType === 'department' || node.data?.partType === 'department'
            }"
                        [ngStyle]="node.selected ? {'opacity': '100%' } : {'opacity': '30%' }"
                    >
                        <!-- use a disabled button to provide padding -->
                        <button
                            mat-icon-button
                            disabled
                            style="align-self: flex-start"
                            [ngClass]="{  'inherit-border-inactive': showInactiveReverseInheritance(node), 
                            'inherit-border-active': hasReverseInheritance(node),
                            'inherit-border-active-selected': hasInheritanceFromSelectedNode(node),
                            'inherit-border-active-hovered': hasInheritanceFromHoveredNode(node)
              }"
                        >
                            <mat-icon class="mat-icon-rtl-mirror"></mat-icon>
                        </button>

                        <div class="node-actions">
                            <!-- If the node has a key, it's a valid position. -->
                            @if (node.key) { @if ( node.type !== 'securityGroup' && node.type !== 'feature' && node.type
                            !== 'featureDetails') {
                            <div class="node-content-wrapper">
                                <span
                                    class="t-10"
                                    [ngStyle]="(node.data?.colour && 
                                   node.data.partType !== 'section' && node.data.partType !== 'unit' &&
                                   node.partType !== 'section' && node.partType !== 'unit') ? {
                          'background-color': node.data.colour.background || 'transparent',
                          'color': node.data.colour.color || 'inherit'
                        } : {}"
                                >
                                    @if (node.partType === 'post' || node.data?.partType === 'post') {
                                    <mat-icon
                                        style="
                                            font-size: 16px;
                                            height: 16px;
                                            width: 16px;
                                            margin-right: 8px;
                                            vertical-align: middle;
                                        "
                                        >school</mat-icon
                                    >
                                    } {{node.name}}
                                </span>
                                @if (data.config.allowCreate) {
                                <button mat-icon-button color="accent" [matMenuTriggerFor]="menu">
                                    <mat-icon>more_vert</mat-icon>
                                </button>
                                }
                            </div>
                            } @if ( node.type === 'securityGroup') {
                            <span class="t-10">{{node.name}} - securityGroup </span>
                            } @if ( node.type === 'feature') {
                            <span class="t-10">{{node.name}} - feature </span>
                            } @if ( node.type === 'featureDetails') {
                            <span class="t-10">{{node.name}} - permissions </span>
                            }

                            <!-- Else if a node doesn't have a key, it's a mock node to indicate the user must create a position to proceed. -->
                            } @else {
                            <!-- Show message and add button. -->
                            @if (data.config.allowCreate) {
                            <span class="t-10">{{node.name}} ***</span>
                            <button mat-icon-button color="accent" (click)="createNewPosition(node, 'sibling');">
                                <mat-icon>add</mat-icon>
                            </button>

                            <!-- Show message that they must contact admin. -->
                            } @else {
                            <span class="t-10"
                                >You aren't allowed to create org parts, please contact your administrator.</span
                            >
                            } }

                            <!-- Spinner while things are loading. -->
                            @if(node.showLoader) {
                            <mat-spinner
                                class="ml-1 orange-spinner"
                                diameter="20"
                                style="display: inline-block; vertical-align: middle"
                            ></mat-spinner>
                            }
                        </div>

                        <mat-menu #menu="matMenu">
                            <button mat-menu-item color="primary" (click)="createNewPosition(node, 'sibling');">
                                <mat-icon color="primary">add</mat-icon>
                                <span>Add Sibling</span>
                            </button>
                            <button mat-menu-item color="accent" (click)="createNewPosition(node, 'child');">
                                <mat-icon color="accent">add</mat-icon>
                                <span>Add Child</span>
                            </button>
                            @if (hasReorderableChildren(node)) {
                            <button mat-menu-item (click)="reorderChildren(node);">
                                <mat-icon>swap_vert</mat-icon>
                                <span>Reorder Children</span>
                            </button>
                            }
                            <mat-divider></mat-divider>
                            <button mat-menu-item (click)="editPosition(node);">
                                <mat-icon>edit</mat-icon>
                                <span>Edit Org Part</span>
                            </button>
                            <button mat-menu-item color="warn" (click)="deletePosition(node);">
                                <mat-icon color="warn">delete</mat-icon>
                                <span>Delete Org Part</span>
                            </button>
                        </mat-menu>
                    </mat-tree-node>

                    <!-- This is the tree node template for expandable nodes -->
                    <mat-tree-node
                        *matTreeNodeDef="let node;when: hasChild"
                        matTreeNodePadding
                        (mouseenter)="onHoverPosition(node)"
                        (click)="onClickPosition(node)"
                        [ngClass]="{
              'is-hovered': node.key === hoveredPosition?.key, 
              'is-selected': node.key === selectedPosition?.key,
              'is-post': node.partType === 'post' || node.data?.partType === 'post',
              'is-executive': node.partType === 'executive' || node.data?.partType === 'executive',
              'has-color': (node.data?.colour || (node.data && node.data.colour)) && 
                          node.data.partType !== 'section' && node.data.partType !== 'unit' &&
                          node.partType !== 'section' && node.partType !== 'unit',
              'is-division': node.partType === 'division' || node.data?.partType === 'division',
              'is-department': node.partType === 'department' || node.data?.partType === 'department'
            }"
                        [ngStyle]="node.selected ? {'opacity': '100%' } : {'opacity': '30%' }"
                    >
                        <button
                            mat-icon-button
                            matTreeNodeToggle
                            style="align-self: flex-start"
                            [id]="'expand-btn-' + node.expandedKey"
                            [attr.aria-label]="'Toggle ' + node.name"
                            [ngClass]="{  'inherit-border-inactive': showInactiveReverseInheritance(node), 
                            'inherit-border-active': hasReverseInheritance(node),
                            'inherit-border-active-selected': hasInheritanceFromSelectedNode(node),
                            'inherit-border-active-hovered': hasInheritanceFromHoveredNode(node)
                }"
                            (click)="onToggleNodeExpand(node, treeControlSecurityGroups.isExpanded(node))"
                        >
                            <mat-icon class="mat-icon-rtl-mirror"
                                >{{treeControlSecurityGroups.isExpanded(node) ? 'expand_more' :
                                'chevron_right'}}</mat-icon
                            >
                        </button>

                        <div class="node-actions">
                            <!-- If the node has a key, it's a valid position. -->
                            @if (node.key) { @if ( node.type !== 'securityGroup' && node.type !== 'feature' && node.type
                            !== 'featureDetails') {
                            <div class="node-content-wrapper">
                                <span
                                    class="t-10"
                                    [ngStyle]="(node.data?.colour && 
                                   node.data.partType !== 'section' && node.data.partType !== 'unit' &&
                                   node.partType !== 'section' && node.partType !== 'unit') ? {
                          'background-color': node.data.colour.background || 'transparent',
                          'color': node.data.colour.color || 'inherit'
                        } : {}"
                                >
                                    @if (node.partType === 'post' || node.data?.partType === 'post') {
                                    <mat-icon
                                        style="
                                            font-size: 16px;
                                            height: 16px;
                                            width: 16px;
                                            margin-right: 8px;
                                            vertical-align: middle;
                                        "
                                        >school</mat-icon
                                    >
                                    } {{node.name}}
                                </span>
                                @if (data.config.allowCreate) {
                                <button mat-icon-button color="accent" [matMenuTriggerFor]="menu">
                                    <mat-icon>more_vert</mat-icon>
                                </button>
                                }
                            </div>
                            } @if ( node.type === 'securityGroup') {
                            <span class="t-10">{{node.name}} - securityGroup </span>
                            } @if ( node.type === 'feature') {
                            <span class="t-10">{{node.name}} - feature </span>
                            } @if ( node.type === 'featureDetails') {
                            <span class="t-10">{{node.name}} - permissions </span>
                            }

                            <!-- Else if a node doesn't have a key, it's a mock node to indicate the user must create a position to proceed. -->
                            } @else {
                            <!-- Show message and add button. -->
                            @if (data.config.allowCreate) {
                            <span class="t-10">{{node.name}}</span>
                            <button mat-icon-button color="accent" (click)="createNewPosition(node, 'sibling');">
                                <mat-icon>add</mat-icon>
                            </button>

                            <!-- Show message that they must contact admin. -->
                            } @else {
                            <span class="t-10"
                                >You aren't allowed to create org parts, please contact your administrator.</span
                            >
                            } } @if(node.showLoader) {
                            <mat-spinner
                                class="ml-1 orange-spinner"
                                diameter="20"
                                style="display: inline-block; vertical-align: middle"
                            ></mat-spinner>
                            }
                        </div>

                        <mat-menu #menu="matMenu">
                            <button mat-menu-item color="primary" (click)="createNewPosition(node, 'sibling');">
                                <mat-icon color="primary">add</mat-icon>
                                <span>Add Sibling</span>
                            </button>
                            <button mat-menu-item color="accent" (click)="createNewPosition(node, 'child');">
                                <mat-icon color="accent">add</mat-icon>
                                <span>Add Child</span>
                            </button>
                            @if (hasReorderableChildren(node)) {
                            <button mat-menu-item (click)="reorderChildren(node);">
                                <mat-icon>swap_vert</mat-icon>
                                <span>Reorder Children</span>
                            </button>
                            }
                            <mat-divider></mat-divider>
                            <button mat-menu-item (click)="editPosition(node);">
                                <mat-icon>edit</mat-icon>
                                <span>Edit Org Part</span>
                            </button>
                            <button mat-menu-item color="warn" (click)="deletePosition(node);">
                                <mat-icon color="warn">delete</mat-icon>
                                <span>Delete Org Part</span>
                            </button>
                        </mat-menu>
                    </mat-tree-node>
                </mat-tree>

                <div class="flex-50 flex-column o-y-scroll o-x-hidden hierarchy-info-panel">
                    <section>
                        <h3>
                            Selected Org Part<strong>{{selectedPosition ? ': ' + selectedPosition.name : ''}}</strong>
                        </h3>

                        @if(selectedPosition) {
                        <div class="position-details is-selected">
                            <h4>
                                Primary:
                                <span class="f-light"
                                    >{{selectedPosition.inheritanceObjects?.primaryPositionSecurityGroups?.length || 0}}
                                    security groups</span
                                >
                            </h4>
                            <div class="pl-1 box-s-left">
                                <ol>
                                    @for(group of (selectedPosition.inheritanceObjects?.primaryPositionSecurityGroups ||
                                    []); track group) {
                                    <li>{{group.name}}</li>
                                    }
                                </ol>
                            </div>

                            <h4>
                                Inheritance:
                                <span class="f-light"
                                    >{{selectedPosition.inheritanceObjects?.secondaryPositionSecurityGroups?.length ||
                                    0}} security groups</span
                                >
                            </h4>
                            @if (selectedPosition.inheritanceObjects?.secondaryPositionSecurityGroups?.length) {
                            <p class="fs-085">
                                {{selectedPosition.name}} will inherit from all the org parts beneath it. Below, the
                                security groups that will be inherited are listed and the bread crumb leads to the org
                                part that the security group is inherited from.
                            </p>
                            } @else {
                            <p class="fs-085">
                                There are no org parts beneath {{selectedPosition.name}}, it does not inherit any
                                security groups.
                            </p>
                            }
                            <div class="pl-1 box-s-left">
                                <!-- List security groups -->
                                <ol>
                                    @for(group of (selectedPosition.inheritanceObjects?.secondaryPositionSecurityGroups
                                    || []); track group) {
                                    <li>
                                        {{group.name}} {{buildBreadCrumb(selectedPosition, group)}}
                                        <!-- List Features -->
                                        <ul>
                                            @for(feature of group.features; track feature) {
                                            <li>{{group.featureDetails[feature].name | normalize}}</li>
                                            <!-- List permissions -->
                                            <ul>
                                                @for(perm of group.featureDetails[feature].permissions; track perm) {
                                                <li>{{perm | normalize}}</li>
                                                }
                                            </ul>
                                            }
                                        </ul>
                                    </li>
                                    }
                                </ol>
                            </div>
                        </div>
                        }
                    </section>

                    <hr class="w-100 hover-legend" />

                    <section class="hover-legend">
                        <h3>Hovered Org Part<strong>{{hoveredPosition ? ': ' + hoveredPosition.name : ''}}</strong></h3>

                        @if(hoveredPosition) {
                        <div class="position-details is-hovered">
                            <h4>
                                Primary:
                                <span class="f-light"
                                    >{{hoveredPosition.inheritanceObjects?.primaryPositionSecurityGroups?.length || 0}}
                                    security groups</span
                                >
                            </h4>
                            <div class="pl-1 box-s-left">
                                <ol>
                                    @for(group of (hoveredPosition.inheritanceObjects?.primaryPositionSecurityGroups ||
                                    []); track group) {
                                    <li>{{group.name}}</li>
                                    }
                                </ol>
                            </div>

                            <h4>
                                Inheritance:
                                <span class="f-light"
                                    >{{hoveredPosition.inheritanceObjects?.secondaryPositionSecurityGroups?.length ||
                                    0}} security groups</span
                                >
                            </h4>
                            @if (hoveredPosition.inheritanceObjects?.secondaryPositionSecurityGroups?.length) {
                            <p class="fs-085">
                                {{hoveredPosition.name}} will inherit from all the org parts beneath it. Below, the
                                security groups that will be inherited are listed and the bread crumb leads to the org
                                part that the security group is inherited from.
                            </p>
                            } @else {
                            <p class="fs-085">
                                There are no org parts beneath {{hoveredPosition.name}}, it does not inherit any
                                security groups.
                            </p>
                            }
                            <div class="pl-1 box-s-left">
                                <!-- List security groups -->
                                <ol>
                                    @for(group of (hoveredPosition.inheritanceObjects?.secondaryPositionSecurityGroups
                                    || []); track group) {
                                    <li>
                                        {{group.name}} {{buildBreadCrumb(hoveredPosition, group)}}
                                        <!-- List Features -->
                                        <ul>
                                            @for(feature of group.features; track feature) {
                                            <li>{{group.featureDetails[feature].name | normalize}}</li>
                                            <!-- List permissions -->
                                            <ul>
                                                @for(perm of group.featureDetails[feature].permissions; track perm) {
                                                <li>{{perm | normalize}}</li>
                                                }
                                            </ul>
                                            }
                                        </ul>
                                    </li>
                                    }
                                </ol>
                            </div>
                        </div>
                        }
                    </section>
                </div>
            </div>
        </div>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Cancel</button>

    <!-- <button mat-stroked-button color="primary" (click)="onSubmit()">Select <mat-icon>check</mat-icon></button> -->
</mat-dialog-actions>
